// src/components/admin/tabs/ErrorsTab.jsx
// 错误中心：错误聚合卡片（点击查样本任务链路）+ AI 任务错误 / 操作错误两栏，
// 支持标记 investigating / resolved / ignored。
// 视觉：工作台语言（Panel / DetailBlock / StatusBadge / font-mono-num），业务逻辑逐字保留。
// props 契约：({ projectId, projectOptions, onProjectSelect, selectedProjectId, onProjectsChanged })
// 本 tab 只用到 projectId / projectOptions，其余由父级传入时忽略（与原实现一致）。
import React, { useCallback, useEffect, useState } from "react";
import {
  fetchErrors,
  fetchErrorGroups,
  fetchAiTaskFullTrace,
  updateAiTaskErrorStatus,
} from "../../../services/adminService";
import { displayUserId, shortText, useAdminScope } from "../adminUtils";
import {
  Panel,
  EmptyText,
  ErrorNotice,
  FilterInput,
  StatusBadge,
  DetailBlock,
  AdminScopeFilterBar,
} from "../adminUi.jsx";

// 处理状态徽章配色（展示语义，不影响取值）
const ERROR_STATUS_COLORS = {
  new: 'var(--color-error)',
  investigating: 'var(--color-warning)',
  resolved: 'var(--color-success)',
  ignored: 'var(--color-text-muted)',
};

// 标记按钮（轻交互 chip）：onClick 逻辑由调用处原样传入
const MarkButton = ({ status, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-7 px-2 rounded-md text-2xs font-medium border cursor-pointer transition-colors duration-150"
    style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
  >
    标记 {status}
  </button>
);

const ErrorsTab = ({ projectId, projectOptions }) => {
  const [filters, setFilters] = useState({ source: '', limit: 100 });
  const [scope, setScope] = useAdminScope(projectId);
  const [data, setData] = useState({ activityErrors: [], taskErrors: [] });
  const [groups, setGroups] = useState({ groups: [] });
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detailRes, groupRes] = await Promise.all([
        fetchErrors({ ...filters, projectId: scope.projectId, userId: scope.userId }),
        fetchErrorGroups({ ...filters, projectId: scope.projectId, userId: scope.userId }),
      ]);
      setData(detailRes);
      setGroups(groupRes);
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || '错误中心加载失败');
    } finally {
      setLoading(false);
    }
  }, [filters, scope.projectId, scope.userId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={load}
        loading={loading}
      >
        <FilterInput label="来源" value={filters.source} onChange={(v) => setFilters(f => ({ ...f, source: v }))} />
        <FilterInput label="条数" type="number" value={filters.limit} onChange={(v) => setFilters(f => ({ ...f, limit: v }))} />
      </AdminScopeFilterBar>
      {error && <ErrorNotice message={error} />}

      <Panel
        title="错误聚合"
        sub="按错误签名分组"
        right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{groups.totalGroups ?? groups.groups?.length ?? 0} 组</span>}
      >
        {!loadedOnce && loading ? (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4">
            <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="skeleton h-20" />)}</div>
            <div className="skeleton h-56" />
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4">
            <div className="space-y-2 max-h-[420px] overflow-y-auto custom-scrollbar">
              {!(groups.groups || []).length ? <EmptyText text="暂无错误聚合" /> : groups.groups.map(group => (
                <button
                  key={group.key}
                  type="button"
                  className="w-full text-left px-3 py-2.5 rounded-md transition-colors duration-150 cursor-pointer bg-[var(--color-bg-overlay)] hover:bg-[var(--color-bg-tertiary)]"
                  style={{ border: '1px solid var(--color-border-secondary)' }}
                  onClick={async () => {
                    if (!group.sampleTaskId) return;
                    setSelectedTrace(await fetchAiTaskFullTrace(group.sampleTaskId));
                  }}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="font-mono-num text-[13px] font-semibold" style={{ color: 'var(--color-error)' }}>{group.count} 次</span>
                    <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{group.sampleTaskId || '-'}</span>
                  </div>
                  <div className="text-xs break-words mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>{group.summary}</div>
                  <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                    项目 {group.projects?.slice?.(0, 4)?.join(', ') || '-'} · 来源 {group.sources?.slice?.(0, 3)?.join(', ') || '-'} · 最近 <span className="font-mono-num">{group.lastSeen ? new Date(group.lastSeen).toLocaleString() : '-'}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="rounded-md p-3" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
              <div className="text-[13px] font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>样本任务链路</div>
              {!selectedTrace?.task ? (
                <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>点击左侧错误组查看样本任务。</div>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="font-mono-num break-all" style={{ color: 'var(--color-info)' }}>{selectedTrace.internalTaskId}</div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      ['日志', selectedTrace.counts?.generationLogs ?? 0],
                      ['费用', selectedTrace.counts?.costRecords ?? 0],
                      ['资源', selectedTrace.counts?.resources ?? 0],
                      ['活动', selectedTrace.counts?.activityLogs ?? 0],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-md p-2" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}>
                        <div className="text-label">{label}</div>
                        <div className="font-mono-num text-sm font-semibold mt-0.5" style={{ color: 'var(--color-text-primary)' }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <DetailBlock title="错误" value={selectedTrace.task.error_message || selectedTrace.task.errorMessage} />
                  <DetailBlock title="Prompt" value={selectedTrace.task.prompt_snapshot || selectedTrace.task.promptSnapshot} />
                </div>
              )}
            </div>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel
          title="AI任务错误"
          right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{data.totalTaskErrors ?? data.taskErrors?.length ?? 0} 条</span>}
        >
          <div className="space-y-2 max-h-[640px] overflow-y-auto custom-scrollbar">
            {!(data.taskErrors || []).length ? <EmptyText text="暂无 AI 任务错误" /> : data.taskErrors.map(task => (
              <div key={task.internal_task_id || task.internalTaskId} className="p-3 rounded-md"
                style={{ backgroundColor: 'var(--color-red-light)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <div className="flex justify-between gap-2 text-xs mb-1">
                  <span className="font-mono-num" style={{ color: 'var(--color-error)' }}>{task.internal_task_id || task.internalTaskId}</span>
                  <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{task.created_at || task.createdAt ? new Date(task.created_at || task.createdAt).toLocaleString() : '-'}</span>
                </div>
                <div className="text-xs mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <span>{displayUserId(task.user_id || task.userId)} · 项目 {task.project_id || task.projectId || '-'} · {task.source || '-'}</span>
                  <StatusBadge label={task.error_status || task.errorStatus || 'new'} color={ERROR_STATUS_COLORS[task.error_status || task.errorStatus || 'new']} />
                </div>
                <div className="text-xs break-words" style={{ color: 'var(--color-text-secondary)' }}>{shortText(task.error_message || task.errorMessage, 420)}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['investigating', 'resolved', 'ignored'].map(status => (
                    <MarkButton
                      key={status}
                      status={status}
                      onClick={async () => {
                        await updateAiTaskErrorStatus(task.internal_task_id || task.internalTaskId, status);
                        await load();
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          title="操作错误"
          right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{data.totalActivityErrors ?? data.activityErrors?.length ?? 0} 条</span>}
        >
          <div className="space-y-2 max-h-[640px] overflow-y-auto custom-scrollbar">
            {!(data.activityErrors || []).length ? <EmptyText text="暂无操作错误" /> : data.activityErrors.map(log => (
              <div key={log.id} className="p-3 rounded-md"
                style={{ backgroundColor: 'var(--color-red-light)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <div className="flex justify-between gap-2 text-xs mb-1">
                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{log.action}</span>
                  <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</span>
                </div>
                <div className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>{displayUserId(log.user_id)} · 项目 {log.project_id || '-'} · {log.provider || '-'}</div>
                <div className="text-xs break-words" style={{ color: 'var(--color-text-secondary)' }}>{shortText(log.error_message, 420)}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default ErrorsTab;
